import { Label } from "@radix-ui/react-label";
import { useEffect, useId, type ComponentProps } from "react";
import {
    useFormContext,
    type FieldValues,
    type Path,
    type PathValue,
} from "react-hook-form";
import { cn } from "@/lib/utils";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { Representative } from "@/v2/types";
import { useSchema } from "@/v2/hooks/SchemaContext";
import { ZodObject } from "zod";
import RequiredMark from "../../ui/form/RequiredMark";

type SelectFormFieldBaseProps = Omit<
    ComponentProps<typeof Select>,
    "name" | "onValueChange" | "value" | "defaultValue"
>;

interface ArSelectFormFieldProps<
    T extends FieldValues,
> extends SelectFormFieldBaseProps {
    name: Path<T>;
    label: string;
    options: Representative[];
    defaultValue?: Representative;
    disabled?: boolean;
}

export function SelectRepresentative<T extends FieldValues>({
    label,
    name,
    options,
    disabled,
    defaultValue,
    ...rest
}: ArSelectFormFieldProps<T>) {
    const {
        setValue,
        watch,
        formState: { errors },
    } = useFormContext<T>();
    const error = errors[name];
    const id = useId();
    const value = watch(name);
    const schema = useSchema();
    const requiredCheck = (schema as ZodObject<any>).shape[name].safeParse("");
    const isRequired = !requiredCheck.success;

    useEffect(() => {
        if (defaultValue) {
            setValue(name, defaultValue as PathValue<T, Path<T>>);
        }
    }, [defaultValue, name, setValue]);

    const handleValueChange = (selectedId: string) => {
        const selectedRep = options.find((opt) => opt.id === selectedId);
        if (selectedRep) {
            setValue(name, selectedRep as PathValue<T, Path<T>>);
        }
    };

    return (
        <div>
            <Label
                htmlFor={id}
                className={cn(
                    "text-sm font-medium",
                    error && "text-destructive",
                )}
            >
                {label}
                {isRequired && <RequiredMark />}
            </Label>
            <Select
                value={value?.id || ""}
                onValueChange={handleValueChange}
                {...rest}
            >
                <SelectTrigger
                    id={id}
                    className={cn(
                        error &&
                            "border-destructive focus-visible:ring-destructive",
                    )}
                >
                    <SelectValue placeholder={`Select ${label}`}>
                        {value?.full_name || `Select ${label}`}
                    </SelectValue>
                </SelectTrigger>
                {!disabled && (
                    <SelectContent>
                        {options.map((opt) => (
                            <SelectItem key={opt.id} value={opt.id}>
                                {opt.full_name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                )}
            </Select>
            {error && (
                <p id={`${id}-error`} className="mt-1 text-xs text-destructive">
                    {error.message as string}
                </p>
            )}
        </div>
    );
}
